import React, { useEffect, useState } from 'react';
import { useMiddleman } from '@hubins/middleman';
import Heading from '@hubins/components/Heading';
import { LoadingBox } from '@hubins/components/LoadingIndicators';
import PortfolioRegistration from '@hubins/components/PortfolioRegistration';
import ProfileRegistration from '@hubins/components/ProfileRegistration';
import SimpleTable from 'modules/Tables/SimpleTable';
import ApprovalProfilePortfolioSummary from '@hubins/components/ApprovalProfilePortfolioSummary';
import FMATable from '@hubins/components/FMATable/FMATable';
import HubinsRisks from 'views/Client/RiskTool/HubinsRisks';
import ExpandableButton from '@hubins/components/ExpandableButton';
import BackofficeProfileLink from '@hubins/components/BackofficeProfileLink';
import ScreeningApprovalModal from 'views/Client/Screening/ApprovalModal';

import __ from 'localisation';

const policyInformationHeaders = [
  {
    Header: '',
    accessor: 'title',
  },
  {
    Header: '',
    accessor: 'value',
  }
];

const getPortfolioRows = (portfolio) => {
  return [
    {
      title: __('creation_date'),
      value: portfolio.start_date || portfolio.created_at,
    },
    {
      title: __('currency'),
      value: portfolio.currency,
    },
  ];
};

const getPolicyHolderRows = (holder) => {
  return [
    {
      title: __('name'),
      value: holder.name ? <BackofficeProfileLink profileUuid={holder.uuid}>{holder.name}</BackofficeProfileLink> : holder.name,
    },
    {
      title: __('nin'),
      value: holder.national_identity_number ? <BackofficeProfileLink profileUuid={holder.uuid}>{holder.national_identity_number}</BackofficeProfileLink> : holder.national_identity_number,
    },
  ];
};

const Portfolio = ({
  portfolio_uuid: portfolioUuid,
  profile_uuid: profileUuid,
  endDate,
  onLoad
}) => {

  const [profile, setProfile] = useState(false);
  const [portfolio, setPortfolio] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const middleman = useMiddleman();

  const fetchData = async () => {
    setPortfolio(false);
    setProfile(false);
    setLoading(true);
    setError(false);
    try {
      const profilePromise = middleman.promiseGet(`/profile/${profileUuid}`);
      const portfolioPromise = middleman.promiseGet(`/portfolio/${portfolioUuid}`);
      const res = await Promise.all([profilePromise, portfolioPromise]);
      setProfile(res[0].data);
      setPortfolio(res[1].data);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (onLoad && !loading) onLoad();
  }, [onLoad, loading]);

  if (loading) return (<LoadingBox />);

  if (error) return (
    <Heading size="2" center className="s-top-xl">
      Something went wrong: {error}
    </Heading>);

  return (
    <div>
      <div id="policy_information" data-page-navigation-anchor={__('policy_information')}>
        <Heading size="4" className="s-bottom-xl s-top-xl">
          {__('policy_information')}
        </Heading>
        <ApprovalProfilePortfolioSummary portfolio={portfolio} portfolioUuid={portfolio?.uuid} />
        <SimpleTable
          headers={policyInformationHeaders}
          data={getPortfolioRows(portfolio)}
        />
      </div>
      <div id="risk_tool" data-page-navigation-anchor={__('risk_tool')}>
        <ExpandableButton heading={__('risk_tool')} className="s-top-xl">
          <FMATable profileUuid={profileUuid} portfolioUuid={portfolio?.uuid} endDate={endDate} withoutSearch />
        </ExpandableButton>
      </div>
      <div id="risk_profile" data-page-navigation-anchor={__('risk_profile')}>
        <ExpandableButton heading={__('risk_profile')}>
          <HubinsRisks portfolioUuid={portfolio?.uuid} endDate={endDate} withoutSearch />
        </ExpandableButton>
      </div>
      <div id="policy_roles" data-page-navigation-anchor={__('policy_roles')}>
        <Heading size="4" className="s-bottom-l s-top-xl">Policy Roles</Heading>
        {portfolio.policy_holder.map((holder, index) => (
          <>
            <Heading size="5" className="s-bottom-s">
              {__('policy_holder')} {index + 1}
            </Heading>
            <SimpleTable
              headers={policyInformationHeaders}
              data={getPolicyHolderRows(holder)}
            />
          </>
        ))}
        {portfolio.life_assured.map((assured, index) => (
          <>
            <Heading size="5" className="s-bottom-s s-top-xl">
              {__('life_assured')} {index + 1}
            </Heading>
            <SimpleTable
              headers={policyInformationHeaders}
              data={getPolicyHolderRows(assured)}
            />
          </>
        ))}
        {portfolio.beneficial.map((beneficial, index) => (
          <>
            <Heading size="5" className="s-bottom-s s-top-xl">
              {__('beneficiary')} {index + 1}
            </Heading>
            <SimpleTable
              headers={policyInformationHeaders}
              data={getPolicyHolderRows(beneficial)}
            />
          </>
        ))}
      </div>
      <div id="profile_attributes" data-page-navigation-anchor={__('profile_attributes')}>
        <Heading size="4" className="s-bottom-xl s-top-xl">
          {__('profile_attributes')}
        </Heading>
        <ProfileRegistration
          profile={profile}
          profileUuid={profileUuid}
          endDate={endDate}
        />
      </div>
      <div id="portfolio_attributes" data-page-navigation-anchor={__('portfolio_attributes')}>
        <Heading size="4" className="s-bottom-xl s-top-xl">
          {__('portfolio_attributes')}
        </Heading>
        <PortfolioRegistration
          profile={profile}
          portfolioUuid={portfolioUuid}
          profileUuid={profileUuid}
          endDate={endDate}
        />
      </div>
      <div id="screening" data-page-navigation-anchor={__('screening')}>
        {profile && <ScreeningApprovalModal client={profile} />}
      </div>
    </div>
  );
};

export default Portfolio;
